import { useState, useEffect, useContext } from 'react';
import { Marker, Polygon, Polyline, Popup } from 'react-leaflet';
import Cookies from 'js-cookie';
import { capitalizeWord } from 'utils/stringUtils';
import WidgetTimeUpdate from 'components/Widgets/WidgetTimeUpdate';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import { ErrorMessage } from 'components/Widgets/ErrorMessage';
import { useAPIData } from 'hooks/useAPIData';
import TextToggle from 'components/UI/TextToggle';
import FullScreenMap from 'components/FullScreenMap';
import L from 'leaflet';
import { toDegrees, toRadians } from 'utils/mathUtils';
import * as d3 from 'd3';
import hurricaneIcon from 'assets/icons/red-hurricane.png';
import * as turf from '@turf/turf';
import { LayoutSizeContext, UniqueWidgetRefreshContext } from 'App';
import { Card, Form, Stack } from 'react-bootstrap';
import { getHurricanesHistorical, getHurricanesLive } from 'services/weather/weather.service';

const parseCoordinate = (value) => {
  const num = parseFloat(value);
  return Number.isFinite(num) ? num : null;
};

const toLatLng = (lat, lng) => {
  const parsedLat = parseCoordinate(lat);
  const parsedLng = parseCoordinate(lng);
  if (parsedLat === null || parsedLng === null) return null;
  return [parsedLat, parsedLng];
};

const HurricanesTropicalStorms = ({ widget, dimensions, preview, sidebarOpen }) => {
  const [fetching, setFetching] = useState(true);
  const [stormString, setStormString] = useState('');
  const [stormNames, setStormNames] = useState([]);
  const [mapCenter, setMapCenter] = useState(() =>
    toLatLng(Cookies.get('latitude'), Cookies.get('longitude')),
  );
  const [historicalData, setHistoricalData] = useState([]);
  const [liveData, setLiveData] = useState([]);
  const [showHistorical, setShowHistorical] = useState(false);
  const [error, setError] = useState({ state: false, message: '' });
  const [hurricaneData, setHurricaneData] = useState([]);
  const layout = useContext(LayoutSizeContext);

  const FusionData = useAPIData({
    successCallback: () => {
      setFetching(false);
    },
    errorCallback: () => {
      setError({ state: true, message: 'Error fetching data' });
      setFetching(false);
    },
    fetchingCallback: () => {
      setFetching(true);
    },
  });

  useEffect(() => {
    if (liveData.length === 0) {
      setHurricaneData(historicalData);
      setShowHistorical(true);
    } else {
      setHurricaneData(liveData);
      setShowHistorical(false);
    }
  }, [liveData, historicalData]);

  const uwf = useContext(UniqueWidgetRefreshContext);
  const ROUTE = 'getHurricanesLive';
  const LOCATION = Cookies.get('city') + '-' + Cookies.get('state');
  const UNIQUEKEY = LOCATION + '-' + ROUTE;

  useEffect(() => {
    const fetchData = async () => {
      try {
        FusionData.forceGet({
          key: ROUTE,
          apiFunction: getHurricanesLive,
        }).then((res) => {
          let fixedData = res.data.result.map((item) => {
            if (item.name.includes('LIVE:') || item.local_storm_name.includes('LIVE:')) return item;
            item.name = 'LIVE: ' + item.name;
            item.local_storm_name = 'LIVE: ' + item.local_storm_name;
            return item;
          });

          if (fixedData.length > 0) {
            setLiveData(fixedData);
            setHurricaneData(fixedData);
            getStormNames(fixedData);
          }

          FusionData.forceGet({
            key: 'getHurricanesHistorical',
            apiFunction: getHurricanesHistorical,
          }).then((res) => {
            if (fixedData.length === 0) {
            } else {
              setStormString('All Live Storms');
              const coords = toLatLng(Cookies.get('latitude'), Cookies.get('longitude'));
              if (coords) setMapCenter(coords);
            }
            setHistoricalData(res.data.result);

            setFetching(false);
          });
        });
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setFetching(false);
      }
    };

    fetchData();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uwf[UNIQUEKEY]]);

  useEffect(() => {
    if (showHistorical) {
      setHurricaneData(historicalData);
      getStormNames(historicalData);
    } else {
      if (liveData.length > 0) {
        setHurricaneData(liveData);
        getStormNames(liveData);
      } else setShowHistorical(true);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showHistorical, historicalData]);

  const getStormNames = (data) => {
    let stormList = [];
    //eslint-disable-next-line array-callback-return
    data.map((item) => {
      const nameAndYear = item.name + ' (' + item.code.split('_')[0] + ')';
      if (!stormList.includes(nameAndYear)) {
        stormList.push(nameAndYear);
      }
    });
    stormList.sort((a, b) => {
      return parseInt(b.split('(')[1].split(')')[0]) - parseInt(a.split('(')[1].split(')')[0]);
    });
    setStormNames(stormList);
    setStormString(stormList[0]);
  };

  useEffect(() => {
    let instances = hurricaneData.filter(
      (item) => item.name + ' (' + item.code.split('_')[0] + ')' === stormString,
    );

    if (instances.length > 0) {
      const coords = toLatLng(instances[0].latitude, instances[0].longitude);
      if (coords) setMapCenter(coords);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stormString]);

  const generateHurricanePath = (stormName) => {
    const stormInstances = hurricaneData.filter(
      (item) => item.name + ' (' + item.code.split('_')[0] + ')' === stormName,
    );

    const pathCoordinates = stormInstances
      .map((stormInstance) => toLatLng(stormInstance.latitude, stormInstance.longitude))
      .filter(Boolean);

    if (pathCoordinates.length < 2) return [];

    return pathCoordinates;
  };

  const getHurricaneInfo = () => {
    const stormInstances = hurricaneData.filter(
      (item) => item.name + ' (' + item.code.split('_')[0] + ')' === stormString,
    );
    const latestUpdate = stormInstances[stormInstances.length - 1];

    if (!latestUpdate) return null;

    const infoRows = [
      {
        label: 'Type',
        value: capitalizeWord(latestUpdate.type),
      },
      {
        label: 'Date',
        value: latestUpdate.locale_time,
      },
      {
        label: 'Category',
        value: latestUpdate.category,
      },
      {
        label: 'Max Wind Speed',
        value: `${parseFloat(latestUpdate.wind_speed).toFixed(1)} mph`,
      },
      {
        label: 'Latitude',
        value: `${latestUpdate.latitude} °`,
      },
      {
        label: 'Longitude',
        value: `${latestUpdate.longitude} °`,
      },
      {
        label: 'Baro Pressure',
        value: `${latestUpdate.central_pressure} in.`,
      },
    ];

    return (
      <Stack gap={2} className="small text-white">
        {infoRows.map(({ label, value }) => (
          <div key={label} className="d-flex justify-content-between align-items-center gap-2">
            <span className="text-secondary text-uppercase fw-semibold">{label}</span>
            <span className="fw-semibold text-end">{value}</span>
          </div>
        ))}
      </Stack>
    );
  };

  const getLocalStormName = (stormName) => {
    let yearStr = stormName.split('(')[1].split(')')[0];
    let instances = hurricaneData.filter((item) => {
      return item.name === stormName.split('(')[0].trim();
    });

    let lastUpdate = instances[instances.length - 1];

    if (lastUpdate?.local_storm_name) return lastUpdate.local_storm_name + ' (' + yearStr + ')';
    else return stormName;
  };

  const addColonToStormName = (name) => {
    const types = [
      'Tropical Depression',
      'Tropical Storm',
      'Hurricane',
      'Typhoon',
      'Severe Tropical Storm',
    ];
    let newName = name;
    types.forEach((type) => {
      if (name.includes(type)) {
        newName = name.replace(type, type + ':');
      }
    });
    return newName;
  };

  const generateLineToOutermostPoint = (currentPoint, prevPoint, radius) => {
    radius *= 0.0176229;
    const lat1 = toRadians(currentPoint[0]);
    const lon1 = toRadians(currentPoint[1]);
    const lat2 = toRadians(prevPoint[0]);
    const lon2 = toRadians(prevPoint[1]);
    if (
      !Number.isFinite(lat1) ||
      !Number.isFinite(lon1) ||
      !Number.isFinite(lat2) ||
      !Number.isFinite(lon2)
    )
      return [];

    // Calculate bearing from prevPoint to currentPoint
    const dLon = lon2 - lon1;
    const y = Math.sin(dLon) * Math.cos(lat2);
    const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
    const initialBearing = Math.atan2(y, x);

    // Perpendicular bearing
    const perpBearing = initialBearing + Math.PI / 2;

    // Length of the perpendicular line in degrees
    const lineLength = (radius / 6378137) * (180 / Math.PI);

    const point1 = [
      toDegrees(lat1 + lineLength * Math.cos(perpBearing)),
      toDegrees(lon1 + lineLength * Math.sin(perpBearing)),
    ];

    const point2 = [
      toDegrees(lat1 - lineLength * Math.cos(perpBearing)),
      toDegrees(lon1 - lineLength * Math.sin(perpBearing)),
    ];

    return [point1, point2];
  };

  const getOutermostSinglePoint = (radius, currentPoint, prevPoint) => {
    radius *= 0.0176229;

    const lat1 = toRadians(currentPoint[0]);
    const lon1 = toRadians(currentPoint[1]);
    const lat2 = toRadians(prevPoint[0]);
    const lon2 = toRadians(prevPoint[1]);
    if (
      !Number.isFinite(lat1) ||
      !Number.isFinite(lon1) ||
      !Number.isFinite(lat2) ||
      !Number.isFinite(lon2)
    )
      return [];

    // Calculate bearing from prevPoint to currentPoint
    const dLon = lon2 - lon1;
    const y = Math.sin(dLon) * Math.cos(lat2);
    const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
    const initialBearing = Math.atan2(y, x);
    const lBearing = initialBearing + Math.PI / 4;
    const rBearing = initialBearing + (7 * Math.PI) / 4;

    const lineLength = (radius / 6378137) * (180 / Math.PI);
    const point1 = [
      toDegrees(lat1 - lineLength * Math.cos(initialBearing)),
      toDegrees(lon1 - lineLength * Math.sin(initialBearing)),
    ];
    const point2 = [
      toDegrees(lat1 - lineLength * Math.cos(lBearing)),
      toDegrees(lon1 - lineLength * Math.sin(lBearing)),
    ];
    const point3 = [
      toDegrees(lat1 - lineLength * Math.cos(rBearing)),
      toDegrees(lon1 - lineLength * Math.sin(rBearing)),
    ];

    return [point3, point1, point2];
  };

  const getInnermostSinglePoint = (radius, currentPoint, prevPoint) => {
    radius *= 0.0176229 * 0.5;

    const lat1 = toRadians(currentPoint[0]);
    const lon1 = toRadians(currentPoint[1]);
    const lat2 = toRadians(prevPoint[0]);
    const lon2 = toRadians(prevPoint[1]);
    if (
      !Number.isFinite(lat1) ||
      !Number.isFinite(lon1) ||
      !Number.isFinite(lat2) ||
      !Number.isFinite(lon2)
    )
      return [];

    // Calculate bearing from prevPoint to currentPoint
    const dLon = lon2 - lon1;
    const y = Math.sin(dLon) * Math.cos(lat2);
    const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
    const initialBearing = Math.atan2(y, x);
    const lBearing = initialBearing + Math.PI / 4;
    const rBearing = initialBearing + (7 * Math.PI) / 4;

    const lineLength = (radius / 6378137) * (180 / Math.PI);
    const point1 = [
      toDegrees(lat1 - lineLength * Math.cos(initialBearing)),
      toDegrees(lon1 - lineLength * Math.sin(initialBearing)),
    ];
    const point2 = [
      toDegrees(lat1 - lineLength * Math.cos(lBearing)),
      toDegrees(lon1 - lineLength * Math.sin(lBearing)),
    ];
    const point3 = [
      toDegrees(lat1 - lineLength * Math.cos(rBearing)),
      toDegrees(lon1 - lineLength * Math.sin(rBearing)),
    ];

    return [point3, point1, point2];
  };

  const getHurricaneLivePredictSize = (item, time, baseTime) => {
    const t = new Date(time);
    const bt = new Date(baseTime);
    //base time to hours diff
    const timeDiff = (t - bt) / 1000 / 60 / 60;

    const tdToMi = (td) => {
      return d3
        .scaleLinear()
        .domain([0, 12, 24, 36, 48, 60, 72, 96, 120])
        .range([0, 30, 45, 61, 77, 93, 114, 167, 236])(td);
    };
    const size = 1609.34 * tdToMi(timeDiff) + 50000;

    return size;
  };

  const generateHurricanePathOutline = (stormName) => {
    let instances = hurricaneData.filter((item, index) => {
      return item.name + ' (' + item.code.split('_')[0] + ')' === stormName;
    });
    let lines = [];

    for (let i = 1; i < instances.length; i++) {
      const current = toLatLng(instances[i].latitude, instances[i].longitude);
      const previous = toLatLng(instances[i - 1].latitude, instances[i - 1].longitude);
      if (!current || !previous) continue;
      const line = generateLineToOutermostPoint(current, previous, 400000);
      if (line.length === 2) lines.push(line);
    }

    if (lines.length === 0) return [];

    // map lines1 and lines2
    let line1 = lines.map((line) => line[0]);
    let line2 = lines.map((line) => line[1]);
    const lastCoords = toLatLng(
      instances[instances.length - 1].latitude,
      instances[instances.length - 1].longitude,
    );
    const prevCoords = toLatLng(
      instances[instances.length - 2]?.latitude,
      instances[instances.length - 2]?.longitude,
    );
    const firstCoords = toLatLng(instances[0].latitude, instances[0].longitude);
    const secondCoords = toLatLng(instances[1]?.latitude, instances[1]?.longitude);

    if (!lastCoords || !prevCoords || !firstCoords || !secondCoords) return [];

    let farEdge = getOutermostSinglePoint(10000 / 0.0176229, lastCoords, prevCoords);
    let closeEdge = getInnermostSinglePoint(10000 / 0.0176229, firstCoords, secondCoords);

    if (farEdge.length < 3 || closeEdge.length < 3) return [];

    return [
      [closeEdge[0][0], closeEdge[0][1]],
      ...closeEdge,
      ...line1,
      ...farEdge,
      ...line2.reverse(),
      [closeEdge[0][0], closeEdge[0][1]],
    ];
  };

  const generateHurricaneCone = (stormName) => {
    let instances = hurricaneData.filter(
      (item) =>
        item.name + ' (' + item.code.split('_')[0] + ')' === stormName && item.type === 'forecast',
    );

    let lines = [];

    for (let i = 1; i < instances.length; i++) {
      const current = toLatLng(instances[i].latitude, instances[i].longitude);
      const previous = toLatLng(instances[i - 1].latitude, instances[i - 1].longitude);
      if (!current || !previous) continue;
      const line = generateLineToOutermostPoint(
        current,
        previous,
        getHurricaneLivePredictSize(instances[i], instances[i].utc_time, instances[0].utc_time),
      );
      if (line.length === 2) lines.push(line);
    }

    if (lines.length === 0) return [];

    // map lines1 and lines2
    let line1 = lines.map((line) => line[0]);
    let line2 = lines.map((line) => line[1]);
    const lastCoords = toLatLng(
      instances[instances.length - 1].latitude,
      instances[instances.length - 1].longitude,
    );
    const prevCoords = toLatLng(
      instances[instances.length - 2]?.latitude,
      instances[instances.length - 2]?.longitude,
    );
    const firstCoords = toLatLng(instances[0].latitude, instances[0].longitude);

    if (!lastCoords || !prevCoords || !firstCoords) return [];

    let farEdge = getOutermostSinglePoint(
      getHurricaneLivePredictSize(
        instances[instances.length - 1],
        instances[instances.length - 1].utc_time,
        instances[0].utc_time,
      ),
      lastCoords,
      prevCoords,
    );

    if (farEdge.length < 3) return [];

    return [firstCoords, ...line1, ...farEdge, ...line2.reverse()];
  };

  const cleanShape = (shape = []) => {
    const filtered = shape.filter(
      (point) => Array.isArray(point) && Number.isFinite(point[0]) && Number.isFinite(point[1]),
    );

    if (filtered.length < 2) return filtered;

    const [firstLat, firstLng] = filtered[0];
    const [lastLat, lastLng] = filtered[filtered.length - 1];

    if (firstLat !== lastLat || firstLng !== lastLng) {
      return [...filtered, filtered[0]];
    }

    return filtered;
  };

  const smoothShape = (shape) => {
    const validShape = cleanShape(shape);
    if (validShape.length < 4) return validShape;
    let polygon = turf.polygon([validShape]);

    let smoothed = turf.polygonSmooth(polygon, { iterations: 3 });

    //append the origin point to the end of the smoothed shape

    let simple = turf.simplify(smoothed, {
      tolerance: 0.5,
      highQuality: true,
    });
    let smoothed2 = turf.polygonSmooth(simple, { iterations: 3 });

    const cleanedShape = turf.union(
      turf.featureCollection([
        turf.polygon(smoothed2.features[0].geometry.coordinates),
        turf.polygon(smoothed2.features[0].geometry.coordinates),
      ]),
    );

    const simple2 = turf.simplify(cleanedShape, {
      tolerance: 0.8,
      highQuality: true,
    });
    const smoothed3 = turf.polygonSmooth(simple2, { iterations: 3 });
    return smoothed3.features[0].geometry.coordinates[0];
  };

  const smoothPolyWithOrigin = (shape) => {
    const validShape = cleanShape(shape);
    if (validShape.length < 4) return validShape;
    let polygon = turf.polygon([validShape]);

    let smoothed = turf.polygonSmooth(polygon, { iterations: 3 });
    const smoothPoly = smoothed.features[0].geometry.coordinates[0];

    return smoothPoly;
  };

  if (error.state && !fetching) {
    return (
      <ErrorMessage
        message={error.message}
        retryRoute={ROUTE}
        onRetry={() => {
          setError({ state: false, message: '' });
          setFetching(true);
        }}
      />
    );
  }

  if (fetching && widget) {
    return (
      <WidgetLoadingAnimation widgetName={'HurricanesTropicalStorms'} dimensions={dimensions} />
    );
  }

  if (fetching) {
    return <WidgetLoadingAnimation />;
  }

  if (liveData.length === 0 && historicalData.length === 0) {
    return (
      <div>
        {widget && <WidgetTimeUpdate route={ROUTE} updateIntervalBasis={180} />}
        <div style={{ padding: 15 }}>No data found.</div>
      </div>
    );
  }

  const LiveStorm = () => {
    if (stormString === 'All Live Storms') {
      const names = hurricaneData
        .map((item) => item.name)
        .reduce((acc, curr) => {
          if (!acc.includes(curr)) acc.push(curr);
          return acc;
        }, []);

      return names.map((item, index) => {
        const codeEntry = hurricaneData.find((storm) => storm.name === item);
        if (!codeEntry || !codeEntry.code) return null;
        const code = codeEntry.code;
        const cone = smoothPolyWithOrigin(
          generateHurricaneCone(item + ' (' + code.split('_')[0] + ')'),
        );
        if (cone.length < 3) return null;
        const closedCone = [...cone, cone[0]];
        return <Polygon key={index} positions={closedCone} color="red" />;
      });
    }
    const cone = smoothPolyWithOrigin(generateHurricaneCone(stormString));
    if (cone.length < 3) return null;
    const closedCone = [...cone, cone[0]];
    return <Polygon positions={closedCone} color="red" />;
  };

  const HistoricalStorm = () => {
    const shape = smoothShape(generateHurricanePathOutline(stormString));
    if (shape.length < 3) return null;
    const closedShape = [...shape, shape[0]];
    return <Polygon positions={closedShape} color="red" />;
  };

  const HurricanePath = () => {
    if (stormString === 'All Live Storms' && !showHistorical) {
      //find all live storms by name
      const names = hurricaneData
        .filter((filter) => filter.name.substring(0, 5) === 'LIVE:')
        .map((item) => item.name)
        .reduce((acc, curr) => {
          if (!acc.includes(curr)) acc.push(curr);
          return acc;
        }, []);

      return names.map((item, index) => {
        const codeEntry = hurricaneData.find((storm) => storm.name === item);
        if (!codeEntry || !codeEntry.code) return null;
        const code = codeEntry.code;
        const path = generateHurricanePath(item + ' (' + code.split('_')[0] + ')');
        if (!path.length) return null;
        return <Polyline key={`polyline-${index}`} positions={path} color="gray" />;
      });
    }
    const path = generateHurricanePath(stormString);
    if (!path.length) return null;
    return <Polyline key={`polyline-1`} positions={path} color="gray" />;
  };

  const HurricaneIcons = () => {
    if (stormString === 'All Live Storms') {
      return hurricaneData.map((item, index) => {
        const coords = toLatLng(item.latitude, item.longitude);
        if (!coords) return null;
        return (
          <Marker
            key={`circle-${index}`}
            position={coords}
            icon={L.icon({
              iconUrl: hurricaneIcon,
              iconSize: [20, 20],
              iconAnchor: [10, 10],
              html: `<span style="filter: invert(100%) sepia(100%) saturate(1000%) hue-rotate(-50deg);" />`,
            })}
            color="red"
          >
            <Popup>
              <div style={{ fontWeight: 'bold' }}>{item.local_storm_name} </div>
              <div>Type: {capitalizeWord(item.type)} </div>
              <div>Date: {item.locale_time} </div>
              <div>Category: {item.category} </div>
              <div>Max Wind Speed: {parseFloat(item.wind_speed).toFixed(1)} mph </div>
              <div>Latitude: {item.latitude} ° </div>
              <div>Longitude: {item.longitude} ° </div>
              <div>Baro Pressure: {item.central_pressure} in. </div>
            </Popup>
          </Marker>
        );
      });
    }

    return hurricaneData
      .filter((item, index) => {
        return item.name + ' (' + item.code.split('_')[0] + ')' === stormString;
      })
      .map((item, index) => {
        const coords = toLatLng(item.latitude, item.longitude);
        if (!coords) return null;
        return (
          <Marker
            key={`circle-${index}`}
            position={coords}
            icon={L.icon({
              iconUrl: hurricaneIcon,
              iconSize: [20, 20],
              iconAnchor: [10, 10],
              html: `<span style="filter: invert(100%) sepia(100%) saturate(1000%) hue-rotate(-50deg);" />`,
            })}
            color="red"
          >
            <Popup>
              <div style={{ fontWeight: 'bold' }}>{item.local_storm_name} </div>
              <div>Type: {capitalizeWord(item.type)} </div>
              <div>Date: {item.locale_time} </div>
              <div>Category: {item.category} </div>
              <div>Max Wind Speed: {parseFloat(item.wind_speed).toFixed(1)} mph </div>
              <div>Latitude: {item.latitude} ° </div>
              <div>Longitude: {item.longitude} ° </div>
              <div>Baro Pressure: {item.central_pressure} in. </div>
            </Popup>
          </Marker>
        );
      });
  };

  const StormSelector = () => {
    if (!stormNames.length) return <div>No storms found</div>;

    return (
      <Form.Select
        value={stormString}
        onChange={(event) => {
          if (event.target.value === '') return;
          setStormString(event.target.value);
        }}
        size="sm"
        className="bg-primary text-white w-100 border-0"
      >
        <option value="">- Select a storm -</option>
        {!showHistorical && <option value="All Live Storms">All Live Storms</option>}
        {stormNames.map((stormName, index) => (
          <option key={index} value={stormName}>
            {addColonToStormName(getLocalStormName(stormName))}
          </option>
        ))}
      </Form.Select>
    );
  };

  const StormDetailsCard = ({
    className = '',
    headerClassName = '',
    bodyClassName = '',
    footerClassName = '',
  } = {}) => (
    <Card bg="dark" text="light" className={`border-0 shadow-sm ${className}`}>
      <Card.Header className={`bg-primary text-white border-0 py-2 ${headerClassName}`}>
        {StormSelector()}
      </Card.Header>
      <Card.Body className={`bg-dark text-white ${bodyClassName}`}>{getHurricaneInfo()}</Card.Body>
      <Card.Footer className={`bg-dark border-0 pt-0 text-white ${footerClassName}`}>
        <Stack direction="horizontal" gap={2} className="flex-wrap align-items-center">
          <TextToggle
            options={['Live', 'Historical']}
            value={showHistorical}
            setValue={setShowHistorical}
            disabled={liveData.length === 0}
          />
          {liveData.length === 0 && (
            <span className="text-muted small">No live storms at this time.</span>
          )}
        </Stack>
      </Card.Footer>
    </Card>
  );

  const HurricaneMap = ({ showMapSat = true, cssStyle }) => {
    return (
      <FullScreenMap
        key={stormString || (showHistorical ? 'historical' : 'live')}
        sidebarOpen={sidebarOpen}
        mapCenter={mapCenter}
        zoom={5}
        preview={preview}
        showMapSat={showMapSat}
        cssStyle={cssStyle}
      >
        {showHistorical && <HistoricalStorm />}
        {!showHistorical && <LiveStorm />}
        <HurricaneIcons />
        <HurricanePath />
      </FullScreenMap>
    );
  };

  if (!dimensions)
    return (
      <div className="d-flex h-100 w-100 flex-column">
        <WidgetTimeUpdate route={ROUTE} updateIntervalBasis={180} />
        {hurricaneData && (
          <div className="d-flex flex-column fs-6 h-100 overflow-hidden gap-3">
            <div className="w-100 h-md h-lg-lg h-xl-xl">
              <HurricaneMap />
            </div>
            <div className="flex-column w-100 overflow-auto h-75">
              <StormDetailsCard />
            </div>
          </div>
        )}
      </div>
    );

  if (dimensions[0] === 2 && dimensions[1] === 1)
    return (
      <div className="d-flex h-100 flex-column">
        <WidgetTimeUpdate route={ROUTE} updateIntervalBasis={180} />
        {hurricaneData && (
          <div className="d-flex flex-row fs-6 h-100 overflow-hidden gap-3">
            <div className="flex-column w-75 overflow-auto h-100">
              <StormDetailsCard />
            </div>
            <div className="w-100">
              <HurricaneMap />
            </div>
          </div>
        )}
      </div>
    );

  if (dimensions[0] === 1 && dimensions[1] === 1)
    return (
      <div className="d-flex flex-column h-100">
        <WidgetTimeUpdate route={ROUTE} updateIntervalBasis={180} />
        {hurricaneData && hurricaneData.length > 0 && (
          <div className="w-100 h-100 position-relative z-1">
            <HurricaneMap
              showMapSat={false}
              cssStyle={{
                position: 'absolute',
                bottom: '4.5rem',
                left: '0.25rem',
                zIndex: 2,
              }}
            />
            <StormDetailsCard
              className="position-absolute bottom-0 start-0 end-0 mx-2 mb-2 bg-dark bg-opacity-75"
              headerClassName="bg-primary bg-opacity-75"
              bodyClassName="bg-opacity-75"
              footerClassName="bg-opacity-75"
            />
          </div>
        )}
      </div>
    );

  if (dimensions[0] === 1 && dimensions[1] === 2)
    return (
      <div className="d-flex h-100 flex-column">
        <WidgetTimeUpdate route={ROUTE} updateIntervalBasis={180} />
        {hurricaneData && (
          <div className="d-flex flex-column fs-6 h-100 overflow-hidden gap-3">
            <div className="h-100 w-100">
              <HurricaneMap />
            </div>
            <div className="flex-column w-100 overflow-auto h-50">
              <StormDetailsCard />
            </div>
          </div>
        )}
      </div>
    );
};

export default HurricanesTropicalStorms;

